import React, { useState, useEffect } from 'react';
import { SLoader, SBadge } from '@avalara/skylab-sdk/react';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import UPLOADED_DOCUMENTS_CONTROLLER from '../../../controllers/uploadedDocuments';

const renderErrorOccurenceLocation = (rowNumber, columnName) => {
    const hasRow = rowNumber != null;
    const hasColumn = !!columnName;

    if (!hasRow && !hasColumn) {
        return null;
    }

    return (
        <code>
            {hasRow && <><LanguageStringResolver string="uploadedDocuments_row" /> {rowNumber}</>}
            {hasRow && hasColumn && ' / '}
            {hasColumn && <><LanguageStringResolver string="uploadedDocuments_column" /> {columnName}</>}
            {': '}
        </code>
    );
};

const renderErrorDetailLine = (key, rowNumber, columnName, message) => (
    <tr key={key}>
        <td style={{ borderBlockEnd: '1px solid var(--color-border-input)' }}>
            <div className="flex align-items-center" style={{ gap: '0.5rem' }}>
                {renderErrorOccurenceLocation(rowNumber, columnName)}
                <span>{message}</span>
            </div>
        </td>
    </tr>
);

const renderSuggestionForConfidence = (confidence) => {
    switch (confidence) {
        case 'Confident':
            return <p><LanguageStringResolver string="uploadedDocuments_confidenceSuggestion_Confident" /></p>;
        case 'LowConfident':
            return <p><LanguageStringResolver string="uploadedDocuments_confidenceSuggestion_LowConfident" /></p>;
        case 'Generic':
            return <p><LanguageStringResolver string="uploadedDocuments_confidenceSuggestion_Generic" /></p>;
        default:
            return null;
    }
};

const FailedDocumentDetails = ({ fileId, languages }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        const fetchErrorDetails = async () => {
            try {
                const response = await UPLOADED_DOCUMENTS_CONTROLLER.getDocumentErrorDetails(fileId);
                setData(response);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchErrorDetails();
    }, [fileId]);

    if (loading) {
        return (
            <div className="flex justify-content-center">
                <SLoader loading className="small" />
            </div>
        );
    }

    if (error || !data) {
        return <p><LanguageStringResolver string="uploadedDocuments_errorLoadingDetails" /></p>;
    }

    const { summary, errorDetails, confidence } = data;
    const detailsWithMessage = (errorDetails || []).filter((detail) => detail && detail.message);
    const hasDetails = detailsWithMessage.length > 0;
    const errorCount = hasDetails
        ? detailsWithMessage.reduce((total, detail) => total + (detail.occurrenceCount > 0 ? detail.occurrenceCount : 1), 0)
        : null;

    return (
        <div className="failed-document-details">
            <div className="failure-summary">
                <h4 className="mt0 mb0"><LanguageStringResolver string="uploadedDocuments_failureSummary" /></h4>
                <p>{summary}</p>
            </div>

            <div className="error-details">
                <div className="flex" style={{ gap: '0.5rem', alignItems: 'baseline' }}>
                    <h4 className="mb0"><LanguageStringResolver string="uploadedDocuments_errorDetailsHeading" /></h4>
                    <SBadge count={errorCount === null ? LANGUAGES_CONTROLLER.format(languages, 'uploadedDocuments_notApplicable') : errorCount} />
                </div>
                {!hasDetails ? (
                    <p><LanguageStringResolver string="uploadedDocuments_noErrorDetailsAvailable" /></p>
                ) : (
                    <>
                        {renderSuggestionForConfidence(confidence)}
                        <div className="error-details-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <table className="s-table">
                                <tbody>
                                    {detailsWithMessage.map((detail, detailIndex) => {
                                        if (detail.occurrenceCount > 0 && detail.occurrences && detail.occurrences.length > 0) {
                                            return detail.occurrences.map((occurrence, occurrenceIndex) =>
                                                renderErrorDetailLine(`${detailIndex}-${occurrenceIndex}`, occurrence.rowNumber, occurrence.columnName, detail.message)
                                            );
                                        }

                                        return renderErrorDetailLine(detailIndex, null, null, detail.message);
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};


export default FailedDocumentDetails;
